import { HelpCircle } from 'lucide-react';
import React, { useCallback, useEffect, useRef } from 'react';
import { Switch } from '../../../../components/ui/switch';
import CustomInput from '../../../../components/CustomInput/CustomInput';
import CustomTextArea from '../../../../components/CustomTextArea/CustomTextArea';
import CustomTooltip from '../../../../components/CustomTooltip/CustomTooltip';
import SearchableDropdown, {
  DropdownItem
} from '../../../../components/SearchableDropdown/SearchableDropdown';
import Intent from '../../../../types/BotBuilder/Intent';
import IntentFormData from '../../../../types/BotBuilder/IntentFormData';
import UserContext from '../../../../context/UserContext/UserContext';
import useAuthRoles from '../../../../hooks/useAuthRoles';
import ChatbotContext from '../../../../context/ChatbotContext/ChatbotContext';
import BotBuilderService from '../../../../services/BotBuilderService/BotBuilderService';

interface MainIntentSettingsProps {
  intent: Intent | null;
  updateFormData: (formData: IntentFormData) => void;
  formData: IntentFormData;
  isLocked?: boolean;
}

function MainIntentSettings({
  intent,
  updateFormData,
  formData,
  isLocked = false
}: Readonly<MainIntentSettingsProps>): React.ReactElement {
  const [topics, setTopics] = React.useState<string[]>([]);
  const [topicsLoading, setTopicsLoading] = React.useState(false);
  const { user } = React.useContext(UserContext);
  const { currentChatbot } = React.useContext(ChatbotContext);
  const previousIntentIdRef = useRef<number | null>(null);
  const { userIsJapetoAdmin } = useAuthRoles();

  const {
    name,
    description,
    category,
    safeguarding,
    safeguardingRecipient,
    locked
  } = formData;

  const handleNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    updateFormData({ ...formData, name: event.target.value });
  };

  const handleDescriptionChange = (
    event: React.ChangeEvent<HTMLTextAreaElement>
  ): void => {
    const value = event.target.value.trim() === '' ? null : event.target.value;
    updateFormData({ ...formData, description: value as any });
  };

  const handleTopicsSearch = useCallback(
    (query: string, page: number): void => {
      if (!user || !currentChatbot) return;

      if (page === 0) {
        setTopicsLoading(true);
      }

      BotBuilderService.getTopics(currentChatbot.id)
        .then((res) => {
          const filteredTopics = res.topics.filter((topic: string) =>
            topic.toLowerCase().includes(query.toLowerCase())
          );
          setTopics(filteredTopics);
        })
        .catch(() => {
          setTopics([]);
        })
        .finally(() => {
          setTopicsLoading(false);
        });
    },
    [user?.id, currentChatbot]
  );

  const handleTopicSelect = (item: DropdownItem): void => {
    updateFormData({ ...formData, category: item.label });
  };

  const handleTopicClear = (): void => {
    updateFormData({ ...formData, category: null });
  };

  const handleCustomValueCreate = (value: string): void => {
    updateFormData({ ...formData, category: value });
    setTopics((prev) => [...prev, value]);
  };

  useEffect(() => {
    if (intent && intent.id !== previousIntentIdRef.current) {
      const hasUserEdits =
        formData.name !==
          (previousIntentIdRef.current ? (intent.name ?? null) : null) ||
        formData.description !==
          (previousIntentIdRef.current ? (intent.description ?? '') : '') ||
        formData.category !==
          (previousIntentIdRef.current ? (intent.category ?? null) : null) ||
        formData.safeguarding !==
          (previousIntentIdRef.current
            ? (intent.safeguarding ?? false)
            : false) ||
        formData.safeguardingRecipient !==
          (previousIntentIdRef.current
            ? (intent.safeguardingRecipient ?? null)
            : null);

      if (!hasUserEdits) {
        updateFormData({
          name: intent.name ?? null,
          description: intent.description ?? '',
          category: intent.category ?? null,
          safeguarding: intent.safeguarding ?? false,
          safeguardingRecipient: intent.safeguardingRecipient ?? null,
          locked: intent.locked ?? false
        });
      }
      previousIntentIdRef.current = intent.id;
    }
  }, [intent?.id, formData]);

  useEffect(() => {
    if (user && currentChatbot) {
      handleTopicsSearch('', 0);
    }
  }, [user, currentChatbot, handleTopicsSearch]);

  const handleSafeguardingRecipientChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    if (intent) {
      const value =
        event.target.value.trim() === '' ? null : event.target.value;
      updateFormData({
        ...formData,
        safeguardingRecipient: value
      });
    }
  };

  const topicItems: DropdownItem[] = topics.map((topic) => ({
    id: topic,
    label: topic
  }));

  const selectedTopic = category ? { id: category, label: category } : null;

  return (
    <div className='flex flex-col items-center justify-center pb-4'>
      <div className='mb-4 w-full'>
        <CustomInput
          name='name'
          label='Name'
          value={name}
          placeholder='Enter question name'
          onChange={handleNameChange}
          fullWidth
          containerClassName='mb-0 w-full [&>label]:text-white [&>label]:font-medium'
          className='bg-white'
          disabled={isLocked}
        />
      </div>

      <div className='mb-4 w-full'>
        <CustomTextArea
          name='description'
          label='Description'
          value={description ?? ''}
          placeholder='Enter a description'
          onChange={handleDescriptionChange}
          fullWidth
          containerClassName='mb-0 w-full [&_label]:text-white [&_label]:font-medium'
          className='bg-white max-h-24 resize-y'
          disabled={isLocked}
        />
      </div>

      <div className='mb-4 w-full'>
        <div className='mb-2'>
          <span className='text-sm font-medium text-white'>Topic</span>
        </div>
        <SearchableDropdown
          items={topicItems}
          selectedItem={selectedTopic}
          onSelect={handleTopicSelect}
          onSearch={handleTopicsSearch}
          placeholder='Enter a topic'
          loading={topicsLoading}
          showClearButton
          onClear={handleTopicClear}
          allowCustomValue
          onCustomValueCreate={handleCustomValueCreate}
          className='w-full'
          triggerClassName='bg-white'
          disabled={isLocked}
        />
      </div>

      <div className='mb-4 w-full'>
        <div className='flex items-center justify-between'>
          <div className='flex items-center gap-2'>
            <span className='text-sm font-medium text-white'>
              Safeguarding question
            </span>
            <CustomTooltip
              content='Chats containing safeguarding questions are flagged for you to follow up.'
              side='right'
            >
              <HelpCircle className='w-4 h-4 text-white cursor-help' />
            </CustomTooltip>
          </div>
          <Switch
            checked={safeguarding}
            onCheckedChange={(checked) => {
              if (intent?.safeguarding !== undefined) {
                updateFormData({ ...formData, safeguarding: checked });
              }
            }}
            disabled={isLocked}
          />
        </div>
      </div>

      {safeguarding && (
        <div className='w-full'>
          <div className='flex items-center mb-2'>
            <span className='text-sm font-medium text-white'>
              Safeguarding recipients
            </span>
            <CustomTooltip
              content='Fill in this field if you want to be alerted when a safeguarding question is triggered. Separate multiple recipients with commas.'
              side='right'
            >
              <HelpCircle className='w-4 h-4 text-white ml-2 cursor-help' />
            </CustomTooltip>
          </div>
          <CustomInput
            name='safeguarding-recipient'
            value={safeguardingRecipient ?? ''}
            placeholder='Email addresses or mobile numbers, separated by commas'
            onChange={handleSafeguardingRecipientChange}
            fullWidth
            containerClassName='mb-0 w-full'
            className='bg-white'
            disabled={isLocked}
          />
        </div>
      )}

      {userIsJapetoAdmin() && (
        <div className='mb-4 w-full mt-4'>
          <div className='flex items-center justify-between'>
            <div className='flex items-center gap-2'>
              <span className='text-sm font-medium text-white'>
                Lock question
              </span>
              <CustomTooltip
                content='Locked questions cannot be edited and can only be unlocked by Japeto Admins.'
                side='right'
              >
                <HelpCircle className='w-4 h-4 text-white cursor-help' />
              </CustomTooltip>
            </div>
            <Switch
              checked={locked}
              onCheckedChange={(checked) => {
                updateFormData({ ...formData, locked: checked });
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
}

export default MainIntentSettings;
